import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { useEnabledAppsOptions } from '@components/newNavigation/hooks';
import { ErrorTypes } from '@fabric-ui/components';
import { emptyObject, QueryKey } from '@fabric-ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@utils/AvRouter';
import { getAppIdFromPathName, getPathNameByAccount } from '@utils/router.utils';
import useHandleError from '../hooks/useHandleError';
import { useTypeNameMap } from '../hooks/UseTypeNameMap';
import { APP_PATHS, getRoleNameByValue, ROLES } from '../types';
import { sharedWorkerService } from '../workers/sharedWorkerService';
import { useAuthContext } from './AuthContextProvider';
import { hasMoreThanOneAppInPayingAccount, useHasAppInAccount } from './AvContext.hooks';
import { AccountType } from './AvContext.types';
import { GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS, GET_UI_CONFIG } from './gql';
import { DataContextType } from './types';
import { useAccount } from './useAccount';
import useEntities from './useEntities';
import { useForceRender } from './utils';

export const DataContext = React.createContext<DataContextType>({
  UIConfig: undefined,
  accountData: {
    id: '',
    name: '',
    apps: [],
    logo: null,
    accountType: AccountType.POC,
    autoScheduleHour: null,
    flags: { isRecommendedFieldsEnabled: false },
    aiFeatures: { enabled: true },
  },
  accountEntities: {} as DataContextType['accountEntities'],
  enabledApps: [],
  getPathName: () => ({ to: '', params: {} }),
  hasAppInAccount: () => false,
  hasMultipleAppsInPayingAccount: false,
  isLoading: true,
  measurements: { all: [], visible: [], asObject: emptyObject },
  calculatedDimensions: { all: [], visible: [], asObject: emptyObject },
  refetchAccountData() {},
  refetchUIConfig() {},
  selectedApp: '',
  setAccountId() {},
  setSelectedApp() {},
  typeNameMap: undefined,
});

export const useMeasurementsAndCalculatedDimensions = (options?) => {
  const queryResult = useGqlQuery<{
    findMeasurementsByAccountId: { measurement; createdByUserId; updatedByUserId; id }[];
    findCalculatedDimensionsByAccountId: { calculatedDimension; createdByUserId; updatedByUserId; id }[];
  }>({
    queryKey: [QueryKey.MeasurementsAndCalculatedDimensions],
    query: GET_MEASUREMENTS_AND_CALCULATED_DIMENSIONS,
    select: (_, data) => data,
    ...options,
  });

  const data = useMemo(() => {
    if (!queryResult.data) {
      return {
        measurements: { all: [], visible: [], asObject: {} },
        calculatedDimensions: { all: [], visible: [], asObject: {} },
      };
    }

    const measurementsData = queryResult.data.findMeasurementsByAccountId || [];
    const calculatedDimensionsData = queryResult.data.findCalculatedDimensionsByAccountId || [];

    // Process measurements
    const measurementsAll = measurementsData.map(v => {
      const { measurement, ...rest } = v;
      return {
        ...measurement,
        ...rest,
        createdByUserId: v.createdByUserId,
        updatedByUserId: v.updatedByUserId,
        id: v.id,
      };
    });

    const measurements = {
      all: measurementsAll,
      visible: measurementsAll.filter(({ hidden }) => !hidden),
      asObject: measurementsAll.reduce((acc, item) => {
        const { systemName, ...rest } = item;
        acc[systemName] = rest;
        return acc;
      }, {}),
    };

    // Process calculated dimensions
    const calculatedDimensionsAll = calculatedDimensionsData.map(v => {
      const { calculatedDimension, ...rest } = v;
      return {
        ...calculatedDimension,
        ...rest,
        // Handle the nested type structure for calculated dimensions
        type: calculatedDimension.type?.kindWrapper?.kind || calculatedDimension.type,
        createdByUserId: v.createdByUserId,
        updatedByUserId: v.updatedByUserId,
        id: v.id,
      };
    });

    const calculatedDimensions = {
      all: calculatedDimensionsAll,
      visible: calculatedDimensionsAll.filter(({ hidden }) => !hidden),
      asObject: calculatedDimensionsAll.reduce((acc, item) => {
        const { systemName, ...rest } = item;
        acc[systemName] = rest;
        return acc;
      }, {}),
    };

    return { measurements, calculatedDimensions };
  }, [queryResult.data]);

  return { ...queryResult, data };
};

export const useMeasurements = (options?) => {
  const result = useMeasurementsAndCalculatedDimensions(options);
  return {
    ...result,
    data: result.data.measurements,
  };
};

const useUiConfig = (options?) =>
  useGqlQuery({
    queryKey: [QueryKey.UiConfig],
    query: GET_UI_CONFIG,
    ...options,
    retry: 10,
    retryDelay: 1000,
  });

export const DataContextProvider = ({ children }: React.PropsWithChildren) => {
  const { accountId, user, userPermissions, isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const handleError = useHandleError();
  const client = useQueryClient();
  const selectedAppId = getAppIdFromPathName(location.pathname) || '';
  const [selectedApp, setSelectedApp] = useState(selectedAppId || '');

  const forceRender = useForceRender();

  const {
    data: accountData,
    refetch: refetchAccountData,
    isPending: isLoadingAccount,
    error: accountError,
  } = useAccount({ enabled: isAuthenticated });

  useEffect(() => {
    if (selectedAppId) {
      setSelectedApp(selectedAppId);
    }
  }, [selectedAppId]);

  const previousAccount = useRef(accountId);

  useEffect(() => {
    if (previousAccount.current !== accountId) {
      switchAccountClearCache(accountId);
      forceRender();
    }

    const handleBeforeUnload = () => {
      client.clear();
      globalThis.isReloading = true;
      sharedWorkerService.notifyWorkerOnUnloadEvent(accountId);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup the event listener on component unmount or when accountId changes
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [accountId]);

  const switchAccountClearCache = id => {
    sharedWorkerService.notifyWorkerOnSwitchAccountEvent(previousAccount.current);
    setSelectedApp('');
    previousAccount.current = id;
    client.clear();
    navigate({ to: '/$accountId', params: { accountId: `${id}` }, ignoreBlocker: true });
  };

  const setAccountId = id => {
    if (window.location.pathname === '/') {
      navigate({ to: '/$accountId', params: { accountId: `${id}` }, ignoreBlocker: true });
    } else {
      switchAccountClearCache(id);
    }
  };

  const {
    data: UIConfig = emptyObject,
    isPending: isLoadingUIConfig,
    refetch: refetchUIConfig,
    error: uiConfigError,
  } = useUiConfig({ enabled: isAuthenticated });
  const { data: typeNameMap, isPending: isLoadingSources, error: connectorsError } = useTypeNameMap({ enabled: isAuthenticated });
  const {
    data: measurementsAndCalculatedDimensions,
    isPending: isLoadingMeasurementsAndCalculatedDimensions,
    error: measurementsAndCalculatedDimensionsError,
  } = useMeasurementsAndCalculatedDimensions({ enabled: isAuthenticated });
  const measurements = measurementsAndCalculatedDimensions?.measurements || { all: [], visible: [], asObject: emptyObject };
  const calculatedDimensions = measurementsAndCalculatedDimensions?.calculatedDimensions || { all: [], visible: [], asObject: emptyObject };

  const { data: accountEntities, isLoading: loadingFields, error: entitiesError } = useEntities({ enabled: isAuthenticated });
  const hasAppInAccount = useHasAppInAccount({ accountData });

  const hasMultipleAppsInPayingAccount = hasMoreThanOneAppInPayingAccount({ accountData });

  const enabledApps = useEnabledAppsOptions({ accountData, hasAllowedPermission: userPermissions?.hasAllowedPermission });

  useEffect(() => {
    const setInitialSelectedApp = () => {
      const userAppsNames = enabledApps.map(app => app.id);
      const userFavorites = user?.favoriteApps.map(app => app.toLowerCase()).filter(app => userAppsNames.includes(app));
      const appListForInitialSelection = userFavorites?.length > 0 ? userFavorites : userAppsNames;
      const userApps = appListForInitialSelection.find(id => userAppsNames.includes(id));
      setSelectedApp(userApps);
    };

    if (user?.roleName && user.roleName === getRoleNameByValue(ROLES.PRE_POV)) {
      setSelectedApp(APP_PATHS.PLATFORM);
    } else if (accountData.apps.length && !selectedApp && user?.roleName) {
      setInitialSelectedApp();
    }
  }, [accountData.apps, user.roleName, enabledApps, accountId]);

  const error = accountError || entitiesError || uiConfigError || connectorsError || measurementsAndCalculatedDimensionsError;
  if (error) {
    handleError(error);
    console.error(ErrorTypes.AvContext, error);
    throw new Error(ErrorTypes.AvContext);
  }

  const value = useMemo(
    () => ({
      setAccountId,
      selectedApp,
      setSelectedApp,
      isLoading: isLoadingMeasurementsAndCalculatedDimensions || isLoadingAccount || isLoadingUIConfig || isLoadingSources || loadingFields,
      accountEntities,
      typeNameMap,
      measurements,
      calculatedDimensions,
      accountData,
      refetchAccountData,
      UIConfig,
      getPathName: getPathNameByAccount(accountId, selectedApp),
      refetchUIConfig,
      hasAppInAccount,
      hasMultipleAppsInPayingAccount,
      enabledApps,
    }),
    [
      loadingFields,
      isLoadingAccount,
      accountEntities,
      typeNameMap,
      measurements,
      calculatedDimensions,
      isLoadingUIConfig,
      accountData,
      UIConfig,
      selectedApp,
      enabledApps,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useDataContext = () => useContext(DataContext);
